/**
 * TopBar - Hive-OS Style Top Bar
 *
 * Features:
 * - Search bar with icon (hidden on mobile)
 * - Control buttons: help, chat toggle, theme toggle
 * - OrgSwitcher integration
 * - User profile dropdown with auth
 */
import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/hooks/useTranslation';
import OrgSwitcher from './OrgSwitcher';
import HelpModal from '../ui/HelpModal';
import * as Icons from '../icons/Icons';

interface TopBarProps {
  isChatOpen: boolean;
  onToggleChat: () => void;
}

export const TopBar: React.FC<TopBarProps> = ({ isChatOpen, onToggleChat }) => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useContext(ThemeContext);
  const { isAuthenticated, currentOrganization } = useAuth();
  const { t } = useTranslation();
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);

  return (
    <>
      <header className="h-16 flex items-center justify-between px-6 lg:px-10 bg-background-panel border-b border-black/5 dark:border-white/5 z-30 shrink-0 transition-all duration-300">
        {/* Left: Search bar - Hidden on mobile */}
        <div className="flex-1 max-w-2xl hidden sm:flex">
          <div className="relative w-full group">
            <Icons.Search className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500 group-focus-within:text-slate-600 dark:group-focus-within:text-white transition-all" />
            <input
              type="text"
              placeholder={t('common.search')}
              className="w-full h-11 bg-black/5 dark:bg-black/30 rounded-xl pl-12 pr-4 text-xs font-bold text-slate-900 dark:text-white border border-black/5 dark:border-white/5 focus:outline-none focus:bg-black/10 dark:focus:bg-black/50 focus:border-blue-500/20 transition-all placeholder:text-slate-400 dark:placeholder:text-slate-400 uppercase tracking-widest"
            />
          </div>
        </div>

        {/* Right: Controls */}
        <div className="flex items-center gap-4 lg:gap-6 ml-6">
          {/* Organization name */}
          {currentOrganization && (
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <Icons.Building className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
              <span className="text-xs font-bold text-amber-700 dark:text-amber-300 max-w-[150px] truncate">
                {currentOrganization.name}
              </span>
            </div>
          )}

          {/* Control buttons group */}
          <div className="flex items-center gap-1.5 bg-black/5 dark:bg-black/20 p-1 rounded-xl border border-black/5 dark:border-white/5 shrink-0">
            {/* Help button */}
            <button
              onClick={() => setIsHelpModalOpen(true)}
              className="p-2 text-slate-500 dark:text-white hover:text-slate-900 dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/10 rounded-lg transition-all"
              title={t('common.helpSupport')}
              aria-label={t('common.helpSupport')}
            >
              <Icons.HelpCircle className="w-4 h-4" />
            </button>

            {/* Chat toggle button */}
            <button
              onClick={onToggleChat}
              className={`p-2 rounded-lg transition-all ${
                isChatOpen
                  ? 'bg-blue-500/10 text-blue-500'
                  : 'text-slate-500 dark:text-white hover:text-slate-900 dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/10'
              }`}
              title={isChatOpen ? t('common.hideChat') : t('common.showChat')}
              aria-label={isChatOpen ? t('common.hideChat') : t('common.showChat')}
            >
              <Icons.MessageCircle className="w-4 h-4" />
            </button>

            {/* Theme toggle button */}
            <button
              onClick={toggleTheme}
              className="p-2 text-slate-500 dark:text-white hover:text-slate-900 dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/10 rounded-lg transition-all"
              title={theme === 'dark' ? t('common.switchToLightMode') : t('common.switchToDarkMode')}
              aria-label={theme === 'dark' ? t('common.switchToLightMode') : t('common.switchToDarkMode')}
            >
              {theme === 'dark' ? (
                <Icons.Sun className="w-4 h-4" />
              ) : (
                <Icons.Moon className="w-4 h-4" />
              )}
            </button>
          </div>

          {/* Organization Switcher */}
          {isAuthenticated && <OrgSwitcher />}

          {/* Sign In Button (only when not authenticated) */}
          {!isAuthenticated && (
            <button
              onClick={() => navigate('/login')}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white text-xs font-bold uppercase tracking-widest shadow-lg shadow-blue-600/20 hover:scale-[1.02] transition-all"
            >
              <Icons.UserIcon className="w-4 h-4" />
              {t('auth.signIn')}
            </button>
          )}
        </div>
      </header>

      <HelpModal isOpen={isHelpModalOpen} onClose={() => setIsHelpModalOpen(false)} />
    </>
  );
};

export default TopBar;
